import type { UtilityContext } from '@change-corgi/core-react/utilityContext';

import type { Session } from '#src/shared/session';

import { treatExperiment } from '#src/app/shared/api/experiments';

import type { PetitionDetailsDataExperimentsQuery } from '../experiments.graphql';
import type { FullAddressCollectionFiltersResult } from '../fullAddressCollectionFilters';

// This is a productized feature but rollout is being controlled via the experiment system

type Experiment = PetitionDetailsDataExperimentsQuery['experiments']['petitionSignFormFullAddressCollectionRollout'];

type Params = {
	experiment: Experiment;
	filters: FullAddressCollectionFiltersResult | undefined;
	loginState: Session['loginState'] | undefined;
	utilityContext: UtilityContext;
};

export async function normalizePetitionSignFormFullAddressCollectionRollout({
	experiment,
	filters,
	loginState,
	utilityContext,
}: Params): Promise<Experiment['variation']> {
	// This feature is disabled for IDENTIFIED users. For details see apps/front-end/src/app/pages/petitionDetails/containers/signForm/README.md
	if (!loginState || loginState === 'IDENTIFIED') {
		return 'control';
	}

	if (shouldRunExperiment({ filters })) {
		await treatExperiment(utilityContext, experiment.experimentName);

		return experiment.variation;
	}

	return 'control';
}

function shouldRunExperiment({ filters }: Pick<Params, 'filters'>): boolean {
	if (!filters) return false;

	return isEnabledByFC(filters) && isEnabledForGeoReach(filters) && hasNonBlockedPoliticianDecisionMaker(filters);
}

function isEnabledByFC(filters: NonNullable<Params['filters']>): boolean {
	return filters.petitionSignFormFullAddressCollection?.enabled === true;
}

function isEnabledForGeoReach(filters: NonNullable<Params['filters']>): boolean {
	return filters.geoReach === 'local';
}

function hasNonBlockedPoliticianDecisionMaker(filters: NonNullable<Params['filters']>): boolean {
	const decisionMakers = filters.dmsWithLegislativeBodiesConnection.nodes.flatMap((node) => node.decisionMakers);

	const blockedDecisionMakerIds = new Set(
		(filters.petitionSignFormFullAddressCollection?.decision_maker_blocklist ?? []).map((dm: { id: string }) => dm.id),
	);

	return decisionMakers.some((dm) => dm.type === 'POLITICIAN' && !blockedDecisionMakerIds.has(dm.id));
}
